import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import logoImage from '../assets/logo.png';
import PasswordToggleIcon from './PasswordToggleIcon';
import './PasswordRecovery.css';

interface RecoveryStep {
  email: string;
  code: string;
  token: string;
  password: string;
  confirmPassword: string;
}

const PasswordRecovery: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<'email' | 'code' | 'password'>('email');
  const [formData, setFormData] = useState<RecoveryStep>({
    email: '',
    code: '',
    token: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [codeDigits, setCodeDigits] = useState<string[]>(['', '', '', '', '', '']);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await apiService.forgotPassword(formData.email);
      setSuccess('Código de recuperação enviado para seu e-mail!');
      setTimeout(() => {
        setCurrentStep('code');
        setSuccess('');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Erro ao enviar código de recuperação');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Verificar se todos os dígitos foram preenchidos
    const fullCode = codeDigits.join('');
    if (fullCode.length !== 6) {
      setError('Por favor, preencha todos os 6 dígitos do código');
      setLoading(false);
      return;
    }

    try {
      // Verificar código e obter token
      const token = await apiService.checkRecoveryCode(formData.email, fullCode);

      // Armazenar token no estado
      setFormData(prev => ({
        ...prev,
        code: fullCode,
        token
      }));

      setSuccess('Código verificado com sucesso! Token obtido.');
      setTimeout(() => {
        setCurrentStep('password');
        setSuccess('');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Código inválido');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validações client-side
    if (!formData.email || !formData.code || !formData.password || !formData.confirmPassword) {
      setError('Todos os campos são obrigatórios');
      return;
    }

    // Verificar se o token foi obtido automaticamente
    if (!formData.token) {
      setError('Token não foi obtido. Verifique o código novamente.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    if (formData.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    // Log para debug
    console.log('Dados sendo enviados:', {
      email: formData.email,
      code: formData.code,
      token: formData.token,
      password: formData.password,
      confirmPassword: formData.confirmPassword
    });

    setLoading(true);
    setError('');

    try {
      await apiService.resetPassword(formData.email, formData.code, formData.token, formData.password);
      setSuccess('Senha alterada com sucesso! Redirecionando para login...');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      console.error('Erro detalhado:', err);
      setError(err.message || 'Erro ao alterar senha');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  const handleCodeChange = (index: number, value: string) => {
    // Permitir apenas números
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newDigits = [...codeDigits];
    newDigits[index] = value;
    setCodeDigits(newDigits);

    // Atualizar formData.code com todos os dígitos concatenados
    const fullCode = newDigits.join('');
    setFormData(prev => ({
      ...prev,
      code: fullCode
    }));

    // Mover foco para o próximo campo se um dígito foi digitado
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-digit-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Se pressionar Backspace e o campo estiver vazio, voltar para o anterior
    if (e.key === 'Backspace' && !codeDigits[index] && index > 0) {
      const prevInput = document.getElementById(`code-digit-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleCodePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    // Verificar se são apenas números e têm 6 dígitos
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setCodeDigits(digits);
      setFormData(prev => ({
        ...prev,
        code: pastedData
      }));

      // Mover foco para o último campo
      const lastInput = document.getElementById('code-digit-5');
      lastInput?.focus();
    }
  };

  const renderEmailStep = () => (
    <div className="recovery-step">
      <div className="step-header">
        <h2>Recuperar Senha</h2>
        <p>Digite seu e-mail para receber o código de recuperação</p>
      </div>

      <form onSubmit={handleEmailSubmit} className="recovery-form">
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="form-group">
          <label htmlFor="email">Endereço de E-mail</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="seu@email.com"
            required
          />
        </div>

        <button
          type="submit"
          className="recovery-button"
          disabled={loading}
        >
          {loading ? 'Enviando...' : 'Enviar Código'}
        </button>

        <div className="form-footer">
          <button
            type="button"
            className="back-to-login"
            onClick={() => navigate('/login')}
          >
            ← Voltar para Login
          </button>
        </div>
      </form>
    </div>
  );

  const renderCodeStep = () => (
    <div className="recovery-step">
      <div className="step-header">
        <h2>Verificar Código</h2>
        <p>Digite o código enviado para {formData.email}</p>
      </div>

      <form onSubmit={handleCodeSubmit} className="recovery-form">
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="form-group">
          <label htmlFor="code-digit-0">Código de Verificação</label>
          <div className="code-input-container">
            {codeDigits.map((digit, index) => (
              <input
                key={index}
                type="text"
                id={`code-digit-${index}`}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleCodeKeyDown(index, e)}
                onPaste={index === 0 ? handleCodePaste : undefined}
                className="code-digit-input"
                autoFocus={index === 0}
                required
              />
            ))}
          </div>
        </div>

        <button
          type="submit"
          className="recovery-button"
          disabled={loading}
        >
          {loading ? 'Verificando...' : 'Verificar Código'}
        </button>

        <div className="form-footer">
          <button
            type="button"
            className="back-to-email"
            onClick={() => {
              setCurrentStep('email');
              setCodeDigits(['', '', '', '', '', '']);
              setFormData(prev => ({ ...prev, code: '' }));
            }}
          >
            ← Voltar para E-mail
          </button>
        </div>
      </form>
    </div>
  );

  const renderPasswordStep = () => (
    <div className="recovery-step">
      <div className="step-header">
        <h2>Nova Senha</h2>
        <p>Digite sua nova senha</p>
      </div>

      <form onSubmit={handlePasswordSubmit} className="recovery-form">
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="form-group">
          <label htmlFor="password">Nova Senha</label>
          <div className="password-input-container">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Digite sua nova senha"
              required
            />
            <button
              type="button"
              className="password-toggle"
              onClick={togglePasswordVisibility}
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            >
              <PasswordToggleIcon isVisible={showPassword} />
            </button>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirmar Nova Senha</label>
          <div className="password-input-container">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder="Confirme sua nova senha"
              required
            />
            <button
              type="button"
              className="password-toggle"
              onClick={toggleConfirmPasswordVisibility}
              aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
            >
              <PasswordToggleIcon isVisible={showConfirmPassword} />
            </button>
          </div>
        </div>

        <button
          type="submit"
          className="recovery-button"
          disabled={loading}
        >
          {loading ? 'Alterando...' : 'Alterar Senha'}
        </button>

        <div className="form-footer">
          <button
            type="button"
            className="back-to-code"
            onClick={() => setCurrentStep('code')}
          >
            ← Voltar para Código
          </button>
        </div>
      </form>
    </div>
  );

  const renderProgressBar = () => (
    <div className="progress-bar">
      <div className={`progress-step ${currentStep === 'email' ? 'active' : ''} ${currentStep === 'code' || currentStep === 'password' ? 'completed' : ''}`}>
        <div className="step-number">1</div>
        <span>E-mail</span>
      </div>
      <div className={`progress-step ${currentStep === 'code' ? 'active' : ''} ${currentStep === 'password' ? 'completed' : ''}`}>
        <div className="step-number">2</div>
        <span>Código</span>
      </div>
      <div className={`progress-step ${currentStep === 'password' ? 'active' : ''}`}>
        <div className="step-number">3</div>
        <span>Nova Senha</span>
      </div>
    </div>
  );

  return (
    <div className="recovery-container">
      <div className="recovery-content">
        <div className="logo-section">
          <img src={logoImage} alt="SendingTo Express Logo" className="recovery-logo" />
        </div>

        {renderProgressBar()}

        <div className="step-content">
          {currentStep === 'email' && renderEmailStep()}
          {currentStep === 'code' && renderCodeStep()}
          {currentStep === 'password' && renderPasswordStep()}
        </div>
      </div>
    </div>
  );
};

export default PasswordRecovery;
